import React from 'react';
import { Box, Typography, Grid, Paper, useMediaQuery, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

import AppMenu from '../components/AppMenu';

import DriveEtaIcon from '@mui/icons-material/DriveEta';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SettingsIcon from '@mui/icons-material/Settings';

type CardInfo = {
    icon: React.ReactNode;
    title: string;
    description: string;
    path: string;
};

const cardData: CardInfo[] = [
    {
        icon: <DriveEtaIcon sx={{ color: '#D558B6', fontSize: 30 }} />,
        title: 'My Events',
        description: 'View and manage events you have created or are hosting.',
        path: '/my-events',
    },
    {
        icon: <AccountCircleIcon sx={{ color: '#D558B6', fontSize: 30 }} />,
        title: 'Account Details',
        description: 'Update your personal information, email, and password.',
        path: '/account-details',
    },
    {
        icon: <EventNoteIcon sx={{ color: '#D558B6', fontSize: 30 }} />,
        title: 'My Bookings',
        description: 'Check your ticket history and upcoming event bookings.',
        path: '/my-bookings',
    },
    {
        icon: <SettingsIcon sx={{ color: '#D558B6', fontSize: 30 }} />,
        title: 'Settings',
        description: 'Customize your notification preferences and privacy settings.',
        path: '/settings',
    },
];

const Account: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <Box width="100vw" overflow="hidden">
            <AppMenu
                onLoginClick={() => {}}
                onSignupClick={() => {}}
                currentPath={location.pathname}
            />
            <Container maxWidth="lg">
                <Box sx={{ p: 3 }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            mb: 4,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            pb: 2,
                            gap: { xs: 2, sm: 0 }
                        }}
                    >
                        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <Typography 
                                variant="h4" 
                                component="h1"
                                sx={{ 
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    mb: 0.5,
                                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
                                }}
                            >
                                My Account
                            </Typography>
                            <Typography 
                                variant="body1" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                    maxWidth: { xs: '80%', sm: 'none' }
                                }}
                            >
                                Manage your account settings and preferences
                            </Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        {cardData.map((card, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 6,
                                        },
                                        position: 'relative',
                                    }}
                                    onClick={() => navigate(card.path)}
                                >
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            border: '2px solid #D558B6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        {card.icon}
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight="bold" align="center">
                                        {card.title}
                                    </Typography>
                                    <Typography variant="body2" align="center" color="textSecondary">
                                        {card.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

export default Account;
